const zlib = require('zlib');
const { ErrorSubcode } = require('solclient-error');
const { Hex } = require('solclient-convert');
const { LOG_TRACE, LOG_DEBUG, LOG_INFO, LOG_WARN } = require('solclient-log');
const { TransportBase } = require('../transport-base');
const { TransportError } = require('../transport-error');
const { TransportReturnCode } = require('../transport-return-codes');
const { TransportSessionEvent } = require('../transport-session-event');
const { TransportSessionEventCode } = require('../transport-session-event-codes');
const { TransportSessionState } = require('../transport-session-states');

const {
  formatHexString,
} = Hex;

/**
 * Modular transport layer performing zlib compression.
 * Requires an underlying transport, can be plaintext or TLS, connected or not.
 * Uses the nodejs zlib module which in turn uses the libuv threadpool in nodejs.
 * The end user may need to increase threadpool size.
 * https://nodejs.org/api/zlib.html#zlib_threadpool_usage
 *
 * @private
 */
class CompressedTransport extends TransportBase {

  constructor(eventCB, client, props) {
    // instanitate instance properties defined in TransportBase
    super('tcp://x', eventCB, client, props);

    /**
     * @private
     * @type {TransportClientStats}
     */
    this._clientstats = null;
    this._underlyingTransport = null;

    /**
     * Maximum amount of send data than can be queued.
     * NodeJS socket.write accepts all writes, growing a
     * user space buffer indefinitely.
     * The TCP transport (same as WS transport)
     * manages the size of that buffer to stay below this limit.
     * @private
     * @type {Number}
     */
    this._sendBufferMaxSize = props.sendBufferMaxSize;

    /*
     * When true, the upper layer was told NO_SPACE and must be notified
     * of the next "drained" event.
     * @private
     * @type {Boolean}
     */
    this._waitForSocketDrain = false;
    this._waitForCompressorDrain = false;

    /*
     * One-shot flush callback.
     * Set on flush(), ran and cleared on socket drain event.
     * @private
     */
    this._flushCallback = null;

    /**
     * Flag to keep track of socket drained state:
     * Set to false when socket.write returns false,
     * (indicating user-space buffering)
     * Set to true on socket drain events.
     * (indicating the user space buffer has flushed to the OS.)
     * @type {Boolean}
     * @private
     */
    this._socketDrained = true;
    this._compressorDrained = true;
    this._interimBuffer = [];

    /**
     * @type {TransportSessionState}
     * @private
     */
    this._state = TransportSessionState.DOWN;

    /**
     * Save the Event in the onError listener
     * Use it in onClose to populate the event.
     * (Tcp socket calls onError with the error then onClose.
     * WS socket calls only onClose and passes the error directly.)
     * @private
     * @type {?Error}
     */
    this._socketError = null;

    /**
     * @type {?string}
     * @private
     */
    this._sessionId = new Date().getTime();

    // Some whitebox tests expect a transport->transportSession nesting.
    this._transportSession = this;

    this._props.transportProtocol = null;

    this._compressor = null;
    this._decompressor = null;

    this._compressionLevel = props.compressionLevel;
  }

  /**
   * @override
   */
  getClientStats() {
    return this._clientstats;
  }

  setClientStats(value) {
    this._clientstats = value;
  }

  setUnderlyingTransport(newUnderlyingTransoprt) {
    this._underlyingTransport = newUnderlyingTransoprt;
  }

  // Underlying transport callback.
  rxDataBuffer(data) {
    // We do not collect Rx stats.
    if (this._state === TransportSessionState.WAITING_FOR_DESTROY) {
      LOG_DEBUG('TcpTransportCompressedSession stray rxDataBuffer, ignoring.');
      return;
    }
    this._decompressor.write(data);
    this._decompressor.flush();
  }

  /**
   * Decompressor callback.
   * @private
   * @param {Buffer} data the data
   */
  onData(data) {
    // We do not collect Rx stats.
    if (this._state === TransportSessionState.WAITING_FOR_DESTROY) {
      LOG_DEBUG('TcpTransportSession stray onData, ignoring.');
      return;
    }
    if (this._client) {
      this._client.rxDataBuffer(data);
    }
  }

  /**
   * Underlying transport callback for SEND_ERROR
   * @private
   * @param {TransportSessionEvent} transportEvent the error
   */
  onError(transportEvent) {
    // Need new state for "waiting for decompressor flush after error"?
    if (this._state === TransportSessionState.WAITING_FOR_DESTROY) {
      LOG_TRACE('Tcp transport is being destroyed, ignore error');
      return;
    }
    this._state = TransportSessionState.WAITING_FOR_DESTROY;

    const onErrorStage2 = () => {
      if (this._eventCB) {
        LOG_TRACE('Decompressor flush returned, passing error to eventCB.');
        this._eventCB(transportEvent);
      } else {
        LOG_TRACE('Decompressor flush returned, but eventCB already cleared.');
      }
    };

    LOG_TRACE('Flushing decompressor on socket error.');
    this._decompressor.flush(zlib.Z_SYNC_FLUSH, onErrorStage2);
  }

  /**
   * Treat any zlib error the same as a socket error,
   * plus close the socket.
   * @private
   * @param {Error} error the error
   */
  onZlibError(error) {
    this._socketError = error;
    this._underlyingTransport.destroy(`Connection closed by zlib error. Message:${error}`,
                                      ErrorSubcode.COMMUNICATION_ERROR);
  }
  /**
   * underlying transport callback for DESTROYED_NOTICE.
   * @private
   * @param {TransportSessionEvent} transportEvent from lower layer
   */
  onClose(transportEvent) {
    if (this._state === TransportSessionState.WAITING_FOR_DESTROY) {
      this._destroy(transportEvent);
    } else {
      this._state = TransportSessionState.CONNECTION_FAILED;
      LOG_TRACE('Flushing decompressor on socket close.');
      const stage2destroy = () => this._destroy(transportEvent);
      this._decompressor.flush(zlib.Z_SYNC_FLUSH, stage2destroy);
    }
  }

  /**
   * underlying transport callback for UP_NOTICE.
   * @private
   * @param {TransportSessionEvent} transportEvent from lower layer
   */
  onOpen(transportEvent) {
    if (this._state !== TransportSessionState.WAITING_FOR_CREATE) {
      LOG_INFO(`compressor stray onOpen when in state ${this._state}, ignoring.`);
      return;
    }
    this._state = TransportSessionState.SESSION_UP;
      // Send the event to the application letting it know that the session is up
    this._eventCB(transportEvent);
  }

  /**
   * Establish underlying transport.
   * @override
   * @returns {TransportReturnCode} The result of this operation
   */
  connect() {
    LOG_TRACE('Initializing compressed transport');
    let rc = TransportReturnCode.OK;
    if (this._state !== TransportSessionState.DOWN) {
      LOG_INFO(`Invalid state for operation: ${TransportSessionState.nameOf(this._state)}`);
      return TransportReturnCode.INVALID_STATE_FOR_OPERATION;
    }
    this._state = TransportSessionState.WAITING_FOR_CREATE;
    if (this._underlyingTransport._state === TransportSessionState.DOWN) {
      rc = this._underlyingTransport.connect();
    } else {
      this._state = TransportSessionState.SESSION_UP;
    }
    try {
      // initialize compressor, decompressor.
      const options = {
        highWaterMark: this._sendBufferMaxSize,
        level:         this._compressionLevel,
        flush:         zlib.Z_SYNC_FLUSH,
      };
      this._compressor =
        zlib.createDeflateRaw(options);
      this._decompressor = zlib.createInflateRaw({ flush: zlib.Z_SYNC_FLUSH });

      this._decompressor.on('data', this.onData.bind(this));
      this._decompressor.on('error', this.onZlibError.bind(this));
      this._compressor.on('error', this.onZlibError.bind(this));
      this._compressor.on('data', this.sendCompressed.bind(this));
      this._compressor.on('drain', this.onCompressorDrain.bind(this));
    } catch (error) {
      LOG_DEBUG(`Error connecting: ${error}`);
      this._state = TransportSessionState.CONNECTION_FAILED;
      throw new TransportError(`Could not create Tcp zlib layer: ${error.message}`,
        error.subcode || ErrorSubcode.CONNECTION_ERROR);
    }
    return rc;
  }

  /**
   * Destroy the underlying transport.
   * Returns transport return code.
   * We'll clean up zlib objects on the DESTROYED_NOTICE.
   * @override
   * @param {?String} msg The message associated with this operation, if any.
   * @param {?ErrorSubcode} subcode The subcode associated with this operation, if any.
   * @returns {TransportReturnCode} The result of this operation
   */
  destroy(msg, subcode) {
    this._state = TransportSessionState.WAITING_FOR_DESTROY;
    return this._underlyingTransport.destroy(msg, subcode);
  }

  // second stage of the underlying transport callback for DESTROYED_NOTICE.
  // We should only ever get here once the underlying transport is destroyed.
  // See onClose.
  _destroy(transportEvent) {
    if (this._state !== TransportSessionState.DOWN) {
      this._state = TransportSessionState.DOWN;
      const onerrorStub = function onerrorStub() { };
      this._waitForSocketDrain = false;
      this._flushCallback = null;
      this._socketDrained = true;
      this._client = null; // Don't accept data.
      this._compressor.removeAllListeners();
      this._compressor.on('error', onerrorStub); // Prevent unhandled errors
      this._decompressor.removeAllListeners();
      this._decompressor.on('error', onerrorStub); // Prevent unhandled errors
      this._compressor.unpipe();
      //https://github.com/nodejs/node/issues/15625
      this._compressor._hadError = true;
      this._compressor.close();
      this._compressor = null;
      this._decompressor.close();
      this._decompressor = null;
    }

    // always send a DESTROYED_NOTICE in response to a destroy() even
    // if we think the transport is already destroyed
    if (this._eventCB) {
      this._eventCB(transportEvent);
      // Release references to other components
      this._eventCB = null;
    }
    return TransportReturnCode.OK;
  }

  /**
   * Force fail the underlying transport's socket to force a failure.
   * Returns transport return code.
   * @override
   * @param {?String} msg The message associated with this operation, if any.
   * @returns {TransportReturnCode} The result of this operation
   */
  forceFailure(msg) {
    if (this._underlyingTransport) {
      return this._underlyingTransport.forceFailure(msg);
    }
    return TransportReturnCode.OK;
  }

  // underlying transport event dispatch
  eventCB(transportEvent) {
    switch (transportEvent.getTransportEventCode()) {
      case TransportSessionEventCode.UP_NOTICE:
        this.onOpen(transportEvent);
        break;
      case TransportSessionEventCode.SEND_ERROR:
        this.onError(transportEvent);
        break;
      case TransportSessionEventCode.DESTROYED_NOTICE:
        this.onClose(transportEvent);
        break;
      case TransportSessionEventCode.CAN_ACCEPT_DATA:
        this.onDrain(transportEvent);
        break;
      default:
        LOG_WARN('Compressing transport layer got unkown event code ' +
          `${transportEvent.getTransportEventCode}`);
    }
  }


  /**
   * @override
   * @description Intended for clean shutdowns.
   * Callback is invoked when all nodejs-buffered data
   * has been flushed to the OS socket.
   */
  flush(callback) {
    const flushStage2 = () => this._underlyingTransport.flush(callback);
    this._compressor.flush(flushStage2);
    return TransportReturnCode.OK;
  }

  /**
   * @override
   * @description Send encoded SMF message
   * Returns transport return code.
   * @param {String} message The encoded SMF message to send
   * @param {?Boolean} [forceAllowEnqueue=false] If true, do not fail due to transport buffer full
   * @returns {TransportReturnCode} The result of this operation
   */
  send(message, forceAllowEnqueue = false) {
    if (this._state !== TransportSessionState.SESSION_UP) {
      // On a lower layer socket error this transport is in WAITING_FOR_DESTROY state
      // until the decompressor flushes.
      // We could respond with NO_SPACE, but we'd have to send a CAN_SEND on reconnect.
      LOG_INFO(`Compressing transport can't send in state ${this._state}`);
      return TransportReturnCode.INVALID_STATE_FOR_OPERATION;
    }
    // Congestion control.
    // We try to avoid pumping too much data into the compressor until the socket drains.
    // We pause()/resume() the compressor while the socket is congested, but still.
    if (forceAllowEnqueue
      || (this._socketDrained && this._compressorDrained)) {
      if (this._interimBuffer.length > 0) {
        LOG_WARN(`Allowing send when interimBuffer is ${this._interimBuffer.length}`);
      }
      this._compressorDrained = this._compressor.write(message, 'ascii');
      this._compressor.flush();
      this._clientstats.bytesWritten += message.length;
      ++this._clientstats.msgWritten;
      return TransportReturnCode.OK;
    }
    // We'll unblock the user on the next onDrain.
    if (!this._socketDrained) {
      this._waitForSocketDrain = true;
    }
    if (!this._compressorDrained) {
      this._waitForCompressorDrain = true;
    }
    return TransportReturnCode.NO_SPACE;
  }

  sendCompressed(data) {
    if (!this._socketDrained) {
      this._interimBuffer.push(data);
      this._compressor.pause();
      return;
    }
    const rc = this._underlyingTransport.send(data);
    switch (rc) {
      case TransportReturnCode.OK:
        this._socketDrained = true;
        break;
      case TransportReturnCode.NO_SPACE:
        this._socketDrained = false;
        this._interimBuffer.push(data);
        this._compressor.pause();
        break;
      case TransportReturnCode.INVALID_STATE_FOR_OPERATION:
        this.onError(new TransportSessionEvent(
            TransportSessionEventCode.SEND_ERROR,
            'Underlying transport in invalid state for send.',
            null,
            ErrorSubcode.CONNECTION_ERROR, null));
        break;
      default:
        LOG_WARN(`Unknown return code from undelying transport ${rc}`);
    }
  }

  /**
   * Underlying transport callback for CAN_SEND.
   * Bind the socket on setup to avoid stray errors from old sockets
   * in quick connect()-destroy()-connect() scenarios.
   * @private
   */
  onDrain() {
    this._socketDrained = true;
    if (this._interimBuffer && this._interimBuffer.length > 0) {
      const resendBuffer = this._interimBuffer;
      this._interimBuffer = [];
      resendBuffer.forEach(data => this.sendCompressed(data));
    }
    if (!this._socketDrained) {
      return;
    }
    this._compressor.resume();
    if (this._flushCallback) {
      LOG_DEBUG('Transport emitting FLUSH after drain.');
      const prevCallback = this._flushCallback;
      this._flushCallback = null;
      prevCallback();
    }

    if (this._waitForSocketDrain) {
      this._waitForSocketDrain = false;
      if (!this._waitForCompressorDrain) {
        this._eventCB(
          new TransportSessionEvent(TransportSessionEventCode.CAN_ACCEPT_DATA,
                                    '', null, 0, this._sessionId));
      }
    }
  }

  onCompressorDrain() {
    this._compressorDrained = true;
    if (this._waitForCompressorDrain) {
      this._waitForCompressorDrain = false;
      if (!this._waitForSocketDrain) {
        this._eventCB(
          new TransportSessionEvent(TransportSessionEventCode.CAN_ACCEPT_DATA,
                                    '', null, 0, this._sessionId));
      }
    }
  }

  /**
   * @override
   */
  getInfoStr() {
    const str = `TcpTransportSession; sid=${formatHexString(this._sessionId)}`;
    return str;
  }

  pause() {
    this._underlyingTransport.pause();
    this._decompressor.pause();
  }

  unpause() {
    this._underlyingTransport.unpause();
    this._decompressor.resume();
  }

}

module.exports.CompressedTransport = CompressedTransport;
